// src/features/players/components/OverviewTab.tsx

import {
  Card, Group, Text, Button, Stack, Box, TextInput, Textarea, Switch, Divider,
} from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { overviewFormSchema } from '../schemas/PlayerForm.schema';
import { useUpdatePlayer } from '../usePlayerMutations';
import { PlayerUtils } from '../PlayerUtils';
import { PLAYER_CONSTRAINTS } from '@/lib/constants/player';
import type { PlayerViewData, PlayerOverviewFormData } from '@/lib/types/Player';

interface OverviewTabProps {
  player: PlayerViewData;
}

export function OverviewTab({ player }: OverviewTabProps) {
  const playerId = player.core.playerId;
  const updateMutation = useUpdatePlayer(playerId);

  const { control, handleSubmit, formState: { isDirty, errors }, reset } = useForm<PlayerOverviewFormData>({
    resolver: zodResolver(overviewFormSchema),
    defaultValues: PlayerUtils.transformToOverviewForm(player),
  });

  const onSubmit = async (data: PlayerOverviewFormData) => {
    try {
      const req = PlayerUtils.transformOverviewFormToUpdate(data, player);
      await updateMutation.mutateAsync(req);
      reset(data);
      notifications.show({
        title: 'Saved',
        message: `Updated "${data.label}"`,
        color: 'green',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  };

  const isPending = updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">Overview</Text>
            <Group gap="sm">
              {isDirty && (
                <Button variant="default" onClick={() => reset()} disabled={isPending}>
                  Discard
                </Button>
              )}
              <Button type="submit" disabled={!isDirty} loading={isPending}>
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="md">
            <LabelField control={control} />
            <DescShortField control={control} />

            <Divider />

            <EnabledField control={control} />

            <Divider label="Social Sharing" labelPosition="left" />

            <AllowSharingField control={control} />
            <SharingToggles control={control} />
          </Stack>
        </Card>
      </Stack>
    </form>
  );
}

// --- Field components ---

function LabelField({ control }: { control: any }) {
  const { field, fieldState } = useController({ control, name: 'label' });
  return (
    <TextInput
      label="Name"
      required
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      description={`${field.value.length}/${PLAYER_CONSTRAINTS.LABEL_MAX}`}
      maxLength={PLAYER_CONSTRAINTS.LABEL_MAX}
    />
  );
}

function DescShortField({ control }: { control: any }) {
  const { field, fieldState } = useController({ control, name: 'descShort' });
  return (
    <Textarea
      label="Description"
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      description={`${field.value.length}/${PLAYER_CONSTRAINTS.DESC_SHORT_MAX}`}
      maxLength={PLAYER_CONSTRAINTS.DESC_SHORT_MAX}
      autosize
      minRows={2}
    />
  );
}

function EnabledField({ control }: { control: any }) {
  const { field } = useController({ control, name: 'enabled' });
  return (
    <Switch
      label={field.value ? 'Active — player is enabled' : 'Inactive — player is disabled'}
      checked={field.value}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
      color={field.value ? 'green' : 'gray'}
    />
  );
}

function AllowSharingField({ control }: { control: any }) {
  const { field } = useController({ control, name: 'allowSharing' });
  return (
    <Switch
      label="Allow Sharing"
      description="Enable social sharing options on the player"
      checked={field.value}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
    />
  );
}

function SharingToggles({ control }: { control: any }) {
  const allowSharing = useController({ control, name: 'allowSharing' });
  const disabled = !allowSharing.field.value;

  const platforms = [
    { name: 'facebook' as const, label: 'Facebook' },
    { name: 'twitter' as const, label: 'X (Twitter)' },
    { name: 'email' as const, label: 'Email' },
  ];

  return (
    <Stack gap="xs" ml="md">
      {platforms.map(({ name, label }) => (
        <SharingToggle key={name} control={control} name={name} label={label} disabled={disabled} />
      ))}
    </Stack>
  );
}

function SharingToggle({ control, name, label, disabled }: {
  control: any; name: string; label: string; disabled: boolean;
}) {
  const { field } = useController({ control, name });
  return (
    <Switch
      label={label}
      checked={field.value}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
      disabled={disabled}
      size="sm"
    />
  );
}
